// Appointment overlap detection, mirroring the server-side guard.
//
// syncWrite's `beforeWrite` hook (PRD.234) rejects double-bookings with
// "This provider already has an appointment during this time." — but web-admin
// writes to local SQLite first, so that rejection arrives during a background
// upload, long after the drawer said "Appointment booked" and closed. The user
// sees a success toast for a booking the server refused.
//
// Running the SAME rule client-side before the insert turns that silent
// divergence into an explanatory message at the moment of booking.
//
// This module is intentionally self-contained (no workspace imports) so it can
// run under the node:test harness.

/** Minimal shape of an existing appointment for overlap purposes. */
export interface AppointmentForConflict {
  id: string;
  provider_id?: string | null;
  patient_id?: string | null;
  /** Clinic-local date, YYYY-MM-DD (zoneless text). */
  date?: string | null;
  /** Clinic-local start, HH:MM[:SS] (zoneless text). */
  start_time?: string | null;
  duration_minutes?: number | null;
  /**
   * `appointment_statuses.type` for this appointment's status, or null when the
   * status row is missing/unresolvable.
   *
   * IMPORTANT: null does NOT mean "ignore". Rows whose `status_id` points at a
   * nonexistent status are still real appointments occupying real time. The
   * server's original INNER JOIN dropped them from conflict detection, which
   * let genuine double-bookings through; both sides now treat unknown as
   * blocking and only an explicit 'terminal' is excluded.
   */
  status_type?: string | null;
}

export interface ConflictCandidate {
  /** Excluded from matching — an appointment never conflicts with itself. */
  id?: string | null;
  providerId?: string | null;
  patientId?: string | null;
  date: string;
  startTime: string;
  durationMinutes: number;
}

export type ConflictKind = 'provider' | 'patient';

export interface AppointmentConflict {
  kind: ConflictKind;
  appointmentId: string;
  /** Clinic-local "HH:MM" bounds of the conflicting appointment. */
  startTime: string;
  endTime: string;
}

/** Minutes past midnight for "HH:MM" / "HH:MM:SS"; null when unparseable. */
export function timeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Inverse of `timeToMinutes`, for display. Wraps past midnight. */
export function minutesToTime(total: number): string {
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Absolute minutes from an arbitrary epoch, so cross-midnight spans compare. */
function absoluteMinutes(date: string, minutesOfDay: number): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const days = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000;
  return days * 1440 + minutesOfDay;
}

/**
 * Find appointments overlapping the candidate.
 *
 * Overlap is half-open, matching the server's SQL exactly: an existing booking
 * conflicts when `existingStart < candidateEnd AND existingEnd > candidateStart`.
 * Back-to-back appointments (one ending exactly as the next begins) do NOT
 * conflict.
 *
 * Callers should pass appointments for the candidate's date AND the day before,
 * so a long booking spilling past midnight is still caught.
 */
export function findAppointmentConflicts(
  candidate: ConflictCandidate,
  existing: AppointmentForConflict[],
): AppointmentConflict[] {
  const startMin = timeToMinutes(candidate.startTime);
  if (startMin === null || !(candidate.durationMinutes > 0)) return [];
  const start = absoluteMinutes(candidate.date, startMin);
  if (start === null) return [];
  const end = start + candidate.durationMinutes;

  const conflicts: AppointmentConflict[] = [];

  for (const row of existing) {
    if (!row.id || row.id === candidate.id) continue;
    // Only an explicit terminal status is excluded — see status_type above.
    if (row.status_type === 'terminal') continue;

    const rowStartMin = timeToMinutes(row.start_time);
    const duration = row.duration_minutes ?? 0;
    if (rowStartMin === null || duration <= 0 || !row.date) continue;
    const rowStart = absoluteMinutes(row.date, rowStartMin);
    if (rowStart === null) continue;
    const rowEnd = rowStart + duration;

    if (!(rowStart < end && rowEnd > start)) continue;

    // Provider takes precedence: it is the more common clash and the more
    // actionable message ("pick another time or another provider").
    const kind: ConflictKind | null =
      candidate.providerId && row.provider_id === candidate.providerId
        ? 'provider'
        : candidate.patientId && row.patient_id === candidate.patientId
          ? 'patient'
          : null;
    if (!kind) continue;

    conflicts.push({
      kind,
      appointmentId: row.id,
      startTime: minutesToTime(rowStartMin),
      endTime: minutesToTime(rowStartMin + duration),
    });
  }

  return conflicts;
}

/**
 * Message for the first conflict. Deliberately concrete — it names the clashing
 * window so the user can act, rather than repeating the server's generic
 * "already has an appointment during this time".
 */
export function describeAppointmentConflict(
  conflict: AppointmentConflict,
  names: { providerName?: string | null; patientName?: string | null } = {},
): string {
  const window = `${conflict.startTime}–${conflict.endTime}`;
  if (conflict.kind === 'provider') {
    const who = names.providerName?.trim() || 'This provider';
    return `${who} already has an appointment from ${window}. Pick another time, provider, or duration.`;
  }
  const who = names.patientName?.trim() || 'This patient';
  return `${who} already has an appointment from ${window}. Pick another time.`;
}
